import React from 'react';
import { Badge } from './ui/badge';
import StandardModal from './StandardModal';
import { SecondaryButton } from './Buttons';
import { Users, Calendar, Clock } from 'lucide-react';
import { formatTime } from '../utils/formatters';
import { formatReadableDate } from '../utils/dateFormatters';

/**
 * Individual teamMember schedule modal
 * Shows their weekly availability + unavailable dates
 */
const TeamScheduleModal = ({ isOpen, onClose, teamMember, timeFormat }) => {
  if (!teamMember) return null;

  const DAYS = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${teamMember.name}'s Schedule`}
      description={teamMember.roleName ? `${teamMember.roleName} availability` : 'Team member availability'}
      size="sm"
      footer={
        <SecondaryButton onClick={onClose} className="w-full">
          Close
        </SecondaryButton>
      }
    >
      <div className="space-y-6">
        {/* Weekly Schedule */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Weekly Schedule
          </h3>
          
          {teamMember.recurringAvailability?.length > 0 ? (
            <div className="space-y-1">
              {DAYS.map(day => {
                const daySchedule = teamMember.recurringAvailability.find(avail => avail.day === day.value);
                
                return (
                  <div key={day.value} className="flex justify-between items-center p-2 rounded bg-gray-50">
                    <span className="font-medium text-sm text-gray-900">{day.label}</span>
                    {daySchedule ? (
                      <span className="text-sm text-gray-600">
                        {formatTime(daySchedule.startTime, timeFormat)} - {formatTime(daySchedule.endTime, timeFormat)}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">Not available</span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No regular schedule set</p>
            </div>
          )}
        </div>

        {/* Unavailable Dates */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Unavailable Dates ({teamMember.unavailableDates?.length || 0})
          </h3>
          
          {teamMember.unavailableDates?.length > 0 ? (
            <div className="grid grid-cols-1 gap-1 max-h-60 overflow-y-auto">
              {teamMember.unavailableDates.sort().map(date => (
                <div key={date} className="p-2 bg-red-50 border border-red-200 rounded text-center">
                  <span className="text-sm font-medium text-red-800">
                    {formatReadableDate(date)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No off days marked</p>
            </div>
          )}
        </div>
      </div>
    </StandardModal>
  );
};

export default TeamScheduleModal;